import React from "react";

import {
  formatMoneyFull,
  formatPercent,
} from "../../../utils/formatters";

export default function CurrentCoverageCard({
  coverage,
  settings,
}) {
  if (!coverage) return null;

  const currency =
    coverage?.currency ||
    settings?.currency ||
    "USD";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            {coverage.name || "Active financing arrangement"}
          </h3>

          <p className="text-sm text-slate-500 mt-1">
            {coverage.provider || "Provider not specified"}
          </p>

          {coverage.policy_number && (
            <p className="text-xs text-slate-400 mt-1">
              Policy: {coverage.policy_number}
            </p>
          )}
        </div>

        <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
          {coverage.status || "ACTIVE"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5">
        <Metric
          label="Annual Premium"
          value={formatMoneyFull(
            coverage.premium,
            currency
          )}
        />

        <Metric
          label="Deductible"
          value={formatMoneyFull(
            coverage.deductible,
            currency
          )}
        />

        <Metric
          label="Policy Limit"
          value={formatMoneyFull(
            coverage.policy_limit,
            currency
          )}
        />

        <Metric
          label="Coinsurance"
          value={formatPercent(
            coverage.coinsurance ?? 1
          )}
        />

        <Metric
          label="Effective Date"
          value={formatDate(
            coverage.effective_date
          )}
        />

        <Metric
          label="Expiration Date"
          value={formatDate(
            coverage.expiration_date
          )}
        />

        <Metric
          label="Provider"
          value={coverage.provider || "—"}
        />

        <Metric
          label="Policy Number"
          value={coverage.policy_number || "—"}
        />
      </div>

      <div className="flex flex-wrap gap-2 mt-5">
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
          Priority {coverage.priority_order ?? 1}
        </span>

        {coverage.is_primary && (
          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
            Primary
          </span>
        )}

        {coverage.coverage_type && (
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
            {formatCoverageType(
              coverage.coverage_type
            )}
          </span>
        )}
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
}) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500">
        {label}
      </p>

      <p className="text-base font-semibold text-slate-900 mt-1">
        {value}
      </p>
    </div>
  );
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(
    `${value}T00:00:00`
  );

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-IE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatCoverageType(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}